import en from '../../../.cache/translations/en.json';
import React, { createContext, useContext, useState, useEffect } from 'react';

// Default locale
const defaultLocale = 'en';

// List of supported locales
const supportedLocales = [
  'en',
  'fr',
  'es',
  'fi',
  'id',
  'ro',
  'he',
  'hu',
  'hy',
  'th',
  'da',
  'de',
  'hr',
  'nl',
  'ar',
  'el',
  'sv',
  'pt',
  'ru',
  'uk',
  'ka',
  'it',
  'tr',
  'vi',
  'sr',
  'is',
  'sl',
  'bg',
  'sk',
  'lv',
  'no',
  'pl',
  'et',
  'cs',
  'lt',
  'pt-br',
  'ms',
  'ja',
  'ko',
  'zh-cn',
  'tj',
  'az',
  'ca',
  'kk',
  'hi',
  'bn',
  'uz',
  'tl',
  'fa',
];

const globalTranslations = { en };

export const IntlContext = createContext();

// Context provider that supplies Intl state to child components
export function IntlProvider({ children, value }) {
  const intl = useIntlProvider(value);
  return <IntlContext.Provider value={intl}>{children}</IntlContext.Provider>;
}

// Dynamic import function - loads translation file on demand
const loadTranslation = async (locale) => {
  try {
    // Only import the specific translation file needed
    const translation = await import(`../../../.cache/translations/${locale}.json`);
    return translation.default || translation;
  } catch (error) {
    console.error(`Failed to load translation for locale: ${locale}`, error);
    // Fallback to default locale if loading fails
    if (locale !== defaultLocale) {
      return loadTranslation(defaultLocale);
    }
    return {};
  }
};

const fetchTranslations = async (locale) => {
  if (!locale) {
    return;
  }
  if (globalTranslations[locale]) {
    return;
  }
  if (supportedLocales.includes(locale)) {
    const translationData = await loadTranslation(locale);
    globalTranslations[locale] || (globalTranslations[locale] = translationData);
  }
};

// Provider hook that creates intl state and methods
function useIntlProvider(initialLocale) {
  const [locale, setLocale] = useState(initialLocale || defaultLocale);

  const [resumeLocale, setResumeLocale] = useState(defaultLocale);
  const [isLoading, setIsLoading] = useState(true);

  // Load translations for current locale
  useEffect(() => {
    // Only fetch if we don't already have this translation loaded
    if (!globalTranslations[locale]) {
      fetchTranslations(locale).then(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [locale]);

  // Load translations for resume locale
  useEffect(() => {
    // Only fetch if we don't already have this translation loaded
    if (!globalTranslations[resumeLocale]) {
      fetchTranslations(resumeLocale).then(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [resumeLocale]);

  // Set resume locale based on app locale
  useEffect(() => {
    resetToDefaultResumeLocale();
  }, [locale]);

  // Update locale whenever new value comes from the provider
  useEffect(() => {
    if (initialLocale && supportedLocales.includes(initialLocale)) {
      setLocale(initialLocale);
    }
  }, [initialLocale]);

  const resetToDefaultResumeLocale = () => {
    if (!locale) {
      setResumeLocale(defaultLocale);
      return;
    }

    // Map specific locales to their resume equivalent
    switch (locale) {
      case 'th':
      case 'no':
        setResumeLocale(defaultLocale);
        break;
      default:
        setResumeLocale(locale);
        break;
    }
  };

  // Change locale and reload page
  const changeLocale = (newLocale) => {
    if (supportedLocales.includes(newLocale)) {
      document.body.style.display = 'none';
      setLocale(newLocale);
      //window.location.reload();
    }
  };

  /**
   * Translation helper function to return the localized string.
   *
   * e.g. t('greeting') // "Bonjour"
   *
   * @param {string} key
   * @param {string} _locale - Locale to override the default (optional)
   */
  function t(key, _locale = locale) {
    // Handle null values
    const currentLocale = _locale || locale || defaultLocale;

    // Get translation from current locale or fall back to default
    const translation = globalTranslations[currentLocale]?.[key] || globalTranslations[defaultLocale]?.[key];

    if (!translation && !isLoading) {
      console.warn(`Translation '${key}' for locale '${currentLocale}' not found.`);
    }

    return (translation || '').trim();
  }

  return {
    locale,
    setLocale: changeLocale,
    isLoading,
    supportedLocales,
    resumeLocale,
    setResumeLocale,
    resetToDefaultResumeLocale,
    t,
  };
}

// Decorator HOC for legacy class components
export const withIntl = (Component) =>
  class Wrapper extends React.Component {
    static async getInitialProps(ctx) {
      const pageProps = Component.getInitialProps && (await Component.getInitialProps(ctx));
      return { ...pageProps };
    }

    render() {
      return <IntlContext.Consumer>{(state) => <Component {...this.props} {...state} />}</IntlContext.Consumer>;
    }
  };

// Component hook to access Intl methods
const useIntl = () => useContext(IntlContext);

export default useIntl;
